const SENSITIVE_HEADER_RE = /^(authorization|cookie|x-device.*|x-api-key)$/i;
/**
 * Query parameters whose values are credentials. Presigned S3 URLs carry the
 * signing material in `X-Amz-*`; our own links use `token`/`key`.
 */
const SENSITIVE_QUERY_RE = /((?:^|[?&])(?:x-amz-signature|x-amz-credential|x-amz-security-token|token|access_token|refresh_token|api_key|apikey|key|sig|signature)=)[^&#\s"']*/gi;
/** HTTP-ish breadcrumb categories that carry a `data.url`. */
const URL_BREADCRUMB_CATEGORIES = new Set(['fetch', 'xhr', 'http']);
/** Strip the entire query string from a URL. */
export function stripQuery(url) {
    const i = url.indexOf('?');
    return i === -1 ? url : url.slice(0, i);
}
/** Redact credential-bearing query values inside any free-form string. */
export function redactUrlSecrets(text) {
    return text.replace(SENSITIVE_QUERY_RE, '$1[REDACTED]');
}
/**
 * `beforeBreadcrumb` hook: drop query strings from HTTP breadcrumb URLs.
 * The SDK records a full URL for every outbound request, and presigned S3
 * URLs put signature credentials in the query. Non-mutating.
 */
export function scrubBreadcrumb(breadcrumb) {
    const url = breadcrumb.data?.['url'];
    if (breadcrumb.category &&
        URL_BREADCRUMB_CATEGORIES.has(breadcrumb.category) &&
        typeof url === 'string') {
        return {
            ...breadcrumb,
            data: { ...breadcrumb.data, url: stripQuery(url) },
        };
    }
    return breadcrumb;
}
/** Delete credential-bearing headers in place. */
function scrubHeaders(request) {
    const headers = request.headers;
    if (!headers)
        return;
    for (const key of Object.keys(headers)) {
        if (SENSITIVE_HEADER_RE.test(key)) {
            delete headers[key];
        }
    }
}
/**
 * `beforeSend` hook: strip credentials before the event ships. Covers request
 * headers, the captured query string, exception messages (a network error on a
 * presigned URL embeds the whole signed URL in its text), event-carried
 * breadcrumbs, and string extras.
 */
export function scrubEvent(event) {
    const request = event.request;
    if (request) {
        scrubHeaders(request);
        if (typeof request.query_string === 'string') {
            request.query_string = redactUrlSecrets(request.query_string);
        }
        if (typeof request.url === 'string') {
            request.url = redactUrlSecrets(request.url);
        }
    }
    for (const value of event.exception?.values ?? []) {
        if (typeof value.value === 'string') {
            value.value = redactUrlSecrets(value.value);
        }
    }
    for (const crumb of event.breadcrumbs ?? []) {
        if (crumb.data && typeof crumb.data['url'] === 'string') {
            crumb.data['url'] = redactUrlSecrets(crumb.data['url']);
        }
    }
    if (event.extra) {
        for (const [key, value] of Object.entries(event.extra)) {
            if (typeof value === 'string') {
                event.extra[key] = redactUrlSecrets(value);
            }
        }
    }
    return event;
}
